import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import mapboxgl from "mapbox-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import {
  SnapPolygonMode,
  SnapPointMode,
  SnapLineMode,
  SnapModeDrawStyles,
  SnapDirectSelect,
} from "mapbox-gl-draw-snap-mode";
import "mapbox-gl/dist/mapbox-gl.css";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import { cropColors } from "../utils/cropColors";
import { getTileServerUrl } from "../utils/tileServerConfig";

// Set your Mapbox access token
mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN || '';

interface County {
  fips: string;
  state: string;
  state_name: string;
  county: string;
  full_name: string;
  centroid?: [number, number] | null;
  county_key?: string;
}

interface Field {
  field_id: string;
  fieldname: string;
  farm_id: string;
  acres: number;
  geojson: string;
  state: string;
  county: string;
  cdl2024?: string; // Optional - only used when creating fields from map
  climate_zone: string;
  created_at: string;
  updated_at: string;
}

interface MapboxMapProps {
  onMapLoad?: (map: mapboxgl.Map) => void;
  isDrawingMode?: boolean;
  isFieldDrawingMode?: boolean;
  isFieldEditingMode?: boolean;
  isSelectMode?: boolean;
  onDrawingModeChange?: (isDrawing: boolean) => void;
  onVisibleCropsChange?: (crops: string[]) => void;
  onFetchFieldsRequest?: (bounds: mapboxgl.LngLatBounds) => void;
  onDrawingBounds?: (bounds: mapboxgl.LngLatBounds) => void;
  onMapReady?: (isReady: boolean) => void;
  onTileLoadingChange?: (isLoading: boolean) => void;
  onMapMove?: () => void;
  onPolygonClick?: (polygon: any) => void;
  onFieldDrawingComplete?: (polygon: any) => void;
  existingFields?: Field[];
  isFieldSelectionMode?: boolean;
  counties?: County[];
}

const MapboxMap = forwardRef<any, MapboxMapProps>(({
  onMapLoad,
  isDrawingMode = false,
  isFieldDrawingMode = false,
  isFieldEditingMode = false,
  isSelectMode = false,
  onDrawingModeChange,
  onVisibleCropsChange,
  onDrawingBounds,
  onMapReady,
  onTileLoadingChange,
  onMapMove,
  onPolygonClick,
  onFieldDrawingComplete,
  existingFields = [],
  isFieldSelectionMode = false
}, ref) => {
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [mapStyle, setMapStyle] = useState("mapbox://styles/mapbox/satellite-v9");
  const [currentCountyKey, setCurrentCountyKey] = useState<string | null>(null);
  const currentCountyKeyRef = useRef<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [mapStyleLoaded, setMapStyleLoaded] = useState(false);

  // Function to update visible crops based on current viewport
  const updateVisibleCrops = () => {
    const countyKey = currentCountyKeyRef.current;
    if (!mapRef.current || !countyKey) {
      console.log('🔍 updateVisibleCrops: mapRef or countyKey missing');
      return;
    }
    
    console.log('🔍 updateVisibleCrops called for county:', countyKey);
    
    try {
      const sourceId = `crops-${countyKey}`;
      console.log('🔍 Querying source:', sourceId);
      
      const features = mapRef.current.querySourceFeatures(sourceId, {
        sourceLayer: "crops"
      });

      console.log('🔍 Found features:', features.length);

      if (features.length > 0) {
        // Update visible crops
        const cropSet = new Set<string>();
        features.forEach((feature: any) => {
          const cropName = feature.properties?.CDL2024;
          console.log('🔍 Feature crop name:', cropName);
          if (cropName) {
            cropSet.add(cropName);
          }
        });

        const visibleCrops = Array.from(cropSet);
        console.log('🔍 Visible crops found:', visibleCrops);
        if (onVisibleCropsChange) {
          onVisibleCropsChange(visibleCrops);
        }
      } else {
        console.log('🔍 No features found in source:', sourceId);
        // Check if source exists
        const source = mapRef.current.getSource(sourceId);
        console.log('🔍 Source exists:', !!source);
        if (source) {
          console.log('🔍 Source type:', source.type);
        }
      }
    } catch (error) {
      console.error('Error updating visible crops:', error);
    }
  };

  // Drawing mode refs
  const isDrawingRef = useRef<boolean>(false);
  const startPointRef = useRef<[number, number] | null>(null);
  const rectangleRef = useRef<mapboxgl.Marker | null>(null);
  const rectangleSourceRef = useRef<string | null>(null);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    loadCountyTiles,
    refreshExistingFields,
    switchToSatelliteMode,
    toggleMapStyle,
    getBounds: () => mapRef.current?.getBounds(),
    fitBounds: (bounds: any, options?: any) => mapRef.current?.fitBounds(bounds, options),
    flyTo: (options: any) => mapRef.current?.flyTo(options),
    setCenter: (center: [number, number]) => mapRef.current?.setCenter(center),
    setZoom: (zoom: number) => mapRef.current?.setZoom(zoom),
    getFieldDrawingPoints: () => {
      // Return the current drawing points if available
      return [];
    },
    completeFieldDrawing: () => {
      // Complete the field drawing if available
      if (onFieldDrawingComplete && startPointRef.current) {
        // Create a simple polygon from the drawing points
        const polygon = {
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [[]]
          },
          properties: {}
        };
        onFieldDrawingComplete(polygon);
      }
    },
    clearFieldDrawing: () => {
      // Clear the field drawing
      if (rectangleRef.current) {
        rectangleRef.current.remove();
        rectangleRef.current = null;
      }
      if (rectangleSourceRef.current && mapRef.current?.getSource(rectangleSourceRef.current)) {
        if (mapRef.current.getLayer('drawing-rectangle')) {
          mapRef.current.removeLayer('drawing-rectangle');
        }
        if (mapRef.current.getLayer('drawing-rectangle-fill')) {
          mapRef.current.removeLayer('drawing-rectangle-fill');
        }
        mapRef.current.removeSource(rectangleSourceRef.current);
        rectangleSourceRef.current = null;
      }
      startPointRef.current = null;
    }
  }));

  // Simplified function to load tiles for a specific county
  const loadCountyTiles = async (countyKey: string) => {
    if (!mapRef.current || !countyKey) {
      return;
    }


    setIsLoading(true);
    if (onTileLoadingChange) {
      onTileLoadingChange(true);
    }

    try {
      // Clear any existing county data
      if (currentCountyKey) {
        const oldSourceId = `crops-${currentCountyKey}`;
        const oldFillLayerId = `crops-fill-${currentCountyKey}`;
        const oldStrokeLayerId = `crops-stroke-${currentCountyKey}`;

        if (mapRef.current.getLayer(oldFillLayerId)) {
          mapRef.current.removeLayer(oldFillLayerId);
        }
        if (mapRef.current.getLayer(oldStrokeLayerId)) {
          mapRef.current.removeLayer(oldStrokeLayerId);
        }
        if (mapRef.current.getSource(oldSourceId)) {
          mapRef.current.removeSource(oldSourceId);
        }
      }

      const tileServerBaseUrl = getTileServerUrl();
      const tileUrl = `${tileServerBaseUrl}/data/${countyKey}/{z}/{x}/{y}.pbf`;

      // Create unique source and layer IDs for this county
      const sourceId = `crops-${countyKey}`;
      const fillLayerId = `crops-fill-${countyKey}`;
      const strokeLayerId = `crops-stroke-${countyKey}`;

      // Add source for this county
      mapRef.current.addSource(sourceId, {
        type: "vector",
        tiles: [tileUrl],
        minzoom: 0,
        maxzoom: 14,
        scheme: "xyz"
      });

      // Add fill layer for this county
      mapRef.current.addLayer({
        id: fillLayerId,
        type: "fill",
        source: sourceId,
        "source-layer": "crops",
        minzoom: 0,
        maxzoom: 22,
        paint: {
            "fill-color": [
              "case",
              ["has", "fillColor"], ["get", "fillColor"],
              "#CCCCCC"
              ],
              "fill-opacity": 0.1
            }
          
      });
      // Add stroke layer for this county
      mapRef.current.addLayer({
        id: strokeLayerId,
        type: "line",
        source: sourceId,
        "source-layer": "crops",
        minzoom: 0,
        maxzoom: 22,
        paint: {
        "line-color": [
          "case",
          ["has", "fillColor"], ["get", "fillColor"],
          "#3bb2d0"
        ],
        "line-width": 1
      }
      });

      setCurrentCountyKey(countyKey);
      currentCountyKeyRef.current = countyKey;
      console.log('🔍 MapboxMap: Set currentCountyKey to:', countyKey);
      console.log('🔍 MapboxMap: currentCountyKeyRef.current:', currentCountyKeyRef.current);
     

      // Check what features are available after a short delay
      setTimeout(() => {
        try {
          updateVisibleCrops();
          
          // Now that tiles are actually loaded, call the loading change callback
          if (onTileLoadingChange) {
            onTileLoadingChange(false);
          }

          // Re-add existing fields layers to ensure they're on top of the new tile layers
          setTimeout(() => {
            addExistingFieldsLayers();
          }, 100);
        } catch (error) {
          console.error('Error checking features:', error);
          // Even if there's an error, still call the loading change callback
          if (onTileLoadingChange) {
            onTileLoadingChange(false);
          }
        }
      }, 1000);

    } catch (error) {
      console.error(`❌ Error loading tiles for ${countyKey}:`, error);
      // If there's an error, still call the loading change callback
      if (onTileLoadingChange) {
        onTileLoadingChange(false);
      }
    } finally {
      setIsLoading(false);
      // Remove the onTileLoadingChange call from here since we're calling it after tiles are loaded
    }
  };

  // Function to add existing fields layers
  const addExistingFieldsLayers = () => {
    if (!mapRef.current || !isFieldSelectionMode || existingFields.length === 0) return;

    // Remove existing fields layer if it exists
    if (mapRef.current.getLayer('existing-fields-fill')) {
      mapRef.current.removeLayer('existing-fields-fill');
    }
    if (mapRef.current.getLayer('existing-fields-stroke')) {
      mapRef.current.removeLayer('existing-fields-stroke');
    }
    if (mapRef.current.getSource('existing-fields')) {
      mapRef.current.removeSource('existing-fields');
    }

    const existingFieldsGeoJSON: any = {
      type: 'FeatureCollection',
      features: existingFields.map(field => ({
        type: 'Feature',
        geometry: JSON.parse(field.geojson),
        properties: {
          id: field.field_id,
          name: field.fieldname,
          acres: field.acres
        }
      }))
    };

    mapRef.current.addSource('existing-fields', {
      type: 'geojson',
      data: existingFieldsGeoJSON
    });

    // Add fill layer for existing fields (blue) - add at the end to be on top
    mapRef.current.addLayer({
      id: 'existing-fields-fill',
      type: 'fill',
      source: 'existing-fields',
      paint: {
        'fill-color': '#2196F3',
        'fill-opacity': 0.3
      }
    });

    // Add stroke layer for existing fields (blue border) - add at the end to be on top
    mapRef.current.addLayer({
      id: 'existing-fields-stroke',
      type: 'line',
      source: 'existing-fields',
      paint: {
        'line-color': '#2196F3',
        'line-width': 1
      }
    });

  };

  // Function to refresh existing fields (can be called from parent)
  const refreshExistingFields = () => {
    if (!mapRef.current || !isFieldSelectionMode) return;
    
    // Remove and re-add existing fields layers to refresh the data
    if (mapRef.current.getLayer('existing-fields-fill')) {
      mapRef.current.removeLayer('existing-fields-fill');
    }
    if (mapRef.current.getLayer('existing-fields-stroke')) {
      mapRef.current.removeLayer('existing-fields-stroke');
    }
    if (mapRef.current.getSource('existing-fields')) {
      mapRef.current.removeSource('existing-fields');
    }
    
    // Re-add the layers with updated data
    addExistingFieldsLayers();
  };

  // Function to clear all county data
  const clearCountyData = () => {
    if (!mapRef.current) return;


    const countyKeyToClear = currentCountyKeyRef.current;
    if (countyKeyToClear) {
      const sourceId = `crops-${countyKeyToClear}`;
      const fillLayerId = `crops-fill-${countyKeyToClear}`;
      const strokeLayerId = `crops-stroke-${countyKeyToClear}`;

      if (mapRef.current.getLayer(fillLayerId)) {
        mapRef.current.removeLayer(fillLayerId);
      } 
      if (mapRef.current.getLayer(strokeLayerId)) {
        mapRef.current.removeLayer(strokeLayerId);
      }
      if (mapRef.current.getSource(sourceId)) {
        mapRef.current.removeSource(sourceId);
      }   

      setCurrentCountyKey(null);
      currentCountyKeyRef.current = null;
    }
  };

  // Drawing mode event handlers
  const handleMouseDown = (e: any) => {
    if (!isDrawingRef.current) return;

    // Prevent default map behavior (panning)
    e.preventDefault();
    e.originalEvent.preventDefault();

    const point: [number, number] = [e.lngLat.lng, e.lngLat.lat];

    // Validate coordinates
    if (typeof point[0] !== 'number' || typeof point[1] !== 'number' ||
        isNaN(point[0]) || isNaN(point[1])) {
      console.error('❌ Invalid mouse coordinates:', point);
      return;
    }

    // Clean up any previous drawing elements
    if (rectangleRef.current) {
      rectangleRef.current.remove();
      rectangleRef.current = null;
    }

    if (rectangleSourceRef.current && mapRef.current!.getSource(rectangleSourceRef.current)) {
      if (mapRef.current!.getLayer('drawing-rectangle')) {
        mapRef.current!.removeLayer('drawing-rectangle');
      }
      if (mapRef.current!.getLayer('drawing-rectangle-fill')) {
        mapRef.current!.removeLayer('drawing-rectangle-fill');
      }
      mapRef.current!.removeSource(rectangleSourceRef.current);
      rectangleSourceRef.current = null;
    }

    startPointRef.current = point;

    // Create a temporary marker to show the start point
    const el = document.createElement('div');
    el.className = 'drawing-point';
    el.style.width = '10px';
    el.style.height = '10px';
    el.style.borderRadius = '50%';
    el.style.backgroundColor = '#ff0000';
    el.style.border = '2px solid white';

    try {
      rectangleRef.current = new mapboxgl.Marker(el)
        .setLngLat(point)
        .addTo(mapRef.current!);
    } catch (error) {
      console.error('❌ Error creating drawing marker:', error);
    }
  };

  const handleMouseUp = (e: any) => {
    if (!isDrawingRef.current || !startPointRef.current) return;

    // Prevent default map behavior
    e.preventDefault();
    e.originalEvent.preventDefault();

    const endPoint: [number, number] = [e.lngLat.lng, e.lngLat.lat];

    // Validate end point coordinates
    if (typeof endPoint[0] !== 'number' || typeof endPoint[1] !== 'number' ||
        isNaN(endPoint[0]) || isNaN(endPoint[1])) {
      console.error('❌ Invalid end point coordinates:', endPoint);
      return;
    }

    const startPoint = startPointRef.current;

    // Calculate bounds
    const bounds = [
      Math.min(startPoint[0], endPoint[0]),
      Math.min(startPoint[1], endPoint[1]),
      Math.max(startPoint[0], endPoint[0]),
      Math.max(startPoint[1], endPoint[1])
    ];

    // Create LngLatBounds for the loadAreaTiles function
    const mapBounds = new mapboxgl.LngLatBounds()
      .extend([bounds[0], bounds[1]])
      .extend([bounds[2], bounds[3]]);

    // Call the drawing bounds callback (this will handle loading tiles for the drawn area)
    if (onDrawingBounds) {
      onDrawingBounds(mapBounds);
    }

    // Clean up drawing elements only (don't reset drawing mode yet)
    if (rectangleRef.current) {
      rectangleRef.current.remove();
      rectangleRef.current = null;
    }

    // Remove rectangle source and layer
    if (rectangleSourceRef.current && mapRef.current!.getSource(rectangleSourceRef.current)) {
      if (mapRef.current!.getLayer('drawing-rectangle')) {
        mapRef.current!.removeLayer('drawing-rectangle');
      }
      if (mapRef.current!.getLayer('drawing-rectangle-fill')) {
        mapRef.current!.removeLayer('drawing-rectangle-fill');
      }
      mapRef.current!.removeSource(rectangleSourceRef.current);
      rectangleSourceRef.current = null;
    }

    // Reset drawing state
    startPointRef.current = null;
    isDrawingRef.current = false;

    // Exit drawing mode automatically after completing a rectangle
    if (onDrawingModeChange) {
      onDrawingModeChange(false);
    }
  };

  const handleMouseMove = (e: any) => {
    if (!isDrawingRef.current || !startPointRef.current) return;

    // Prevent default map behavior (panning)
    e.preventDefault();
    e.originalEvent.preventDefault();

    const currentPoint: [number, number] = [e.lngLat.lng, e.lngLat.lat];
    const startPoint = startPointRef.current;

    // Create rectangle coordinates
    const coordinates = [
      [startPoint[0], startPoint[1]],
      [currentPoint[0], startPoint[1]],
      [currentPoint[0], currentPoint[1]],
      [startPoint[0], currentPoint[1]],
      [startPoint[0], startPoint[1]] // Close the polygon
    ];

    const rectangleData: GeoJSON.Feature<GeoJSON.Polygon> = {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [coordinates]
      },
      properties: {}
    };

    // Update or create rectangle source
    if (rectangleSourceRef.current && mapRef.current!.getSource(rectangleSourceRef.current)) {
      (mapRef.current!.getSource(rectangleSourceRef.current) as any).setData(rectangleData);
    } else {
      const sourceId = 'drawing-rectangle-source';
      rectangleSourceRef.current = sourceId;

      mapRef.current!.addSource(sourceId, {
        type: 'geojson',
        data: rectangleData
      });

      mapRef.current!.addLayer({
        id: 'drawing-rectangle',
        type: 'line',
        source: sourceId,
        paint: {
          'line-color': '#dc3545',
          'line-width': 3,
          'line-dasharray': [4, 4]
        }
      });

      // Add fill layer for better visibility
      mapRef.current!.addLayer({
        id: 'drawing-rectangle-fill',
        type: 'fill',
        source: sourceId,
        paint: {
          'fill-color': '#dc3545',
          'fill-opacity': 0.1
        }
      });
    }
  };

  // Field drawing state
  const fieldDrawingPointsRef = useRef<[number, number][]>([]);
  const fieldDrawingLineRef = useRef<mapboxgl.Marker | null>(null);
  const fieldDrawingMarkersRef = useRef<mapboxgl.Marker[]>([]);
  const fieldDrawingDrawRef = useRef<MapboxDraw | null>(null);

  // Expose field drawing points to parent component
  const getFieldDrawingPoints = () => {
    return [...fieldDrawingPointsRef.current];
  };

  // Clear field drawing (for starting fresh)
  const clearFieldDrawing = () => {
    if (!mapRef.current) return;

    // Clear points
    fieldDrawingPointsRef.current = [];
    
    // Remove all field drawing markers
    fieldDrawingMarkersRef.current.forEach(marker => {
      if (marker) {
        marker.remove();
      }
    });
    fieldDrawingMarkersRef.current = [];
    
    if (fieldDrawingLineRef.current) {
      fieldDrawingLineRef.current.remove();
      fieldDrawingLineRef.current = null;
    }
    
    // Clean up MapboxDraw edit state if it exists
    if (fieldDrawingDrawRef.current && mapRef.current) {
      try {
        // Clear all features from the draw tool
        fieldDrawingDrawRef.current.deleteAll();
        
        // Remove the draw control from the map
        mapRef.current.removeControl(fieldDrawingDrawRef.current);
        
        // Clean up any remaining gl-draw layers
        if (mapRef.current.isStyleLoaded()) {
          const allLayers = mapRef.current.getStyle().layers || [];
          const glDrawLayers = allLayers.filter((layer: any) => 
            layer.id && typeof layer.id === 'string' && layer.id.startsWith('gl-draw-')
          );
          glDrawLayers.forEach((layer: any) => {
            try {
              if (mapRef.current && mapRef.current.getLayer(layer.id)) {
                mapRef.current.removeLayer(layer.id);
              }
            } catch (e) {
              // Layer might already be removed
            }
          });
        }
      } catch (error) {
        console.warn('Error cleaning up MapboxDraw:', error);
      }
      
      // Reset the draw reference
      fieldDrawingDrawRef.current = null;
    }
    
    // Remove field drawing layers
    if (mapRef.current.getLayer('field-drawing-line-layer')) {
      mapRef.current.removeLayer('field-drawing-line-layer');
    }
    if (mapRef.current.getSource('field-drawing-line')) {
      mapRef.current.removeSource('field-drawing-line');
    }
    if (mapRef.current.getLayer('field-drawing-polygon-layer')) {
      mapRef.current.removeLayer('field-drawing-polygon-layer');
    }
    if (mapRef.current.getLayer('field-drawing-polygon-stroke-layer')) {
      mapRef.current.removeLayer('field-drawing-polygon-stroke-layer');
    }
    if (mapRef.current.getSource('field-drawing-polygon')) {
      mapRef.current.removeSource('field-drawing-polygon');
    }
  };

  const handleFieldDrawingClick = (e: any) => {
    if (!mapRef.current || !isFieldDrawingMode) return;

    const point = e.lngLat;
    const newPoint: [number, number] = [point.lng, point.lat];
    
    // Check if clicking near the first point to close the polygon
    if (fieldDrawingPointsRef.current.length >= 3) {
      const firstPoint = fieldDrawingPointsRef.current[0];
      const distance = Math.sqrt(
        Math.pow(newPoint[0] - firstPoint[0], 2) + 
        Math.pow(newPoint[1] - firstPoint[1], 2)
      );
      
      // If clicking within ~50 meters of the first point, close the polygon
      if (distance < 0.001) { // Roughly 50 meters in degrees
        completeFieldDrawing();
        return;
      }
    }

    fieldDrawingPointsRef.current.push(newPoint);

    // Add a small circle marker
    const pointMarker = new mapboxgl.Marker({
      element: createSmallCircleElement(),
      anchor: 'center'
    }).setLngLat([point.lng, point.lat]).addTo(mapRef.current);

    // Store the marker for cleanup
    fieldDrawingMarkersRef.current.push(pointMarker);

    // Update the drawing visualization
    updateFieldDrawingVisualization();
  };

  const updateFieldDrawingVisualization = () => {
    if (!mapRef.current) return;

    const points = fieldDrawingPointsRef.current;
    
    // Remove existing visualization
    if (mapRef.current.getLayer('field-drawing-line-layer')) {
      mapRef.current.removeLayer('field-drawing-line-layer');
    }
    if (mapRef.current.getSource('field-drawing-line')) {
      mapRef.current.removeSource('field-drawing-line');
    }
    if (mapRef.current.getLayer('field-drawing-polygon-layer')) {
      mapRef.current.removeLayer('field-drawing-polygon-layer');
    }
    if (mapRef.current.getLayer('field-drawing-polygon-stroke-layer')) {
      mapRef.current.removeLayer('field-drawing-polygon-stroke-layer');
    }
    if (mapRef.current.getSource('field-drawing-polygon')) {
      mapRef.current.removeSource('field-drawing-polygon');
    }

    if (points.length >= 2) {
      // Create line connecting all points
      const lineGeoJSON = {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: points
        },
        properties: {}
      };

      // Add the line
      mapRef.current.addSource('field-drawing-line', {
        type: 'geojson',
        data: lineGeoJSON as any
      });

      mapRef.current.addLayer({
        id: 'field-drawing-line-layer',
        type: 'line',
        source: 'field-drawing-line',
        paint: {
          'line-color': '#FF0000',
          'line-width': 2,
          'line-dasharray': [1, 1]
        }
      });
    }

    // If we have 3 or more points, show the polygon
    if (points.length >= 3) {
      const polygonPoints = [...points];
      if (points.length > 3) {
        // Close the polygon by connecting back to the first point
        polygonPoints.push(points[0]);
      }

      const polygonGeoJSON = {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [polygonPoints]
        },
        properties: {}
      };

      // Add the polygon
      mapRef.current.addSource('field-drawing-polygon', {
        type: 'geojson',
        data: polygonGeoJSON as any
      });

      mapRef.current.addLayer({
        id: 'field-drawing-polygon-layer',
        type: 'fill',
        source: 'field-drawing-polygon',
        paint: {
          'fill-color': '#FF0000',
          'fill-opacity': 0.2
        }
      });

      mapRef.current.addLayer({
        id: 'field-drawing-polygon-stroke-layer',
        type: 'line',
        source: 'field-drawing-polygon',
        paint: {
          'line-color': '#FF0000',
          'line-width': 2
        }
      });
    }
  };

  const createSmallCircleElement = () => {
    const element = document.createElement('div');
    element.style.width = '4px';
    element.style.height = '4px';
    element.style.borderRadius = '50%';
    element.style.backgroundColor = '#FF0000';
    element.style.border = '1px solid #FFFFFF';
    element.style.boxShadow = '0 0 2px rgba(0,0,0,0.3)';
    return element;
  };

  const completeFieldDrawing = () => {
    if (!mapRef.current || fieldDrawingPointsRef.current.length < 3) return;

    // Close the polygon by connecting back to the first point
    const points = [...fieldDrawingPointsRef.current];
    points.push(points[0]); // Close the polygon

    // Create the polygon GeoJSON
    const polygonGeoJSON = {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [points]
      },
      properties: {}
    };

    // Call the callback to pass the polygon data to the parent component
    if (onFieldDrawingComplete) {
      onFieldDrawingComplete(polygonGeoJSON);
    }
    
    // Disable field drawing mode
    if (onDrawingModeChange) {
      onDrawingModeChange(false);
    }
  };

  const handleFieldDrawingComplete = (e: any) => {
    if (!mapRef.current || !isFieldDrawingMode || fieldDrawingPointsRef.current.length < 3) return;

    e.preventDefault();

    // Close the polygon by connecting back to the first point
    const points = [...fieldDrawingPointsRef.current];
    if (points.length >= 3) {
      points.push(points[0]); // Close the polygon

      // Create the polygon GeoJSON
      const polygonGeoJSON = {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [points]
        },
        properties: {}
      };

      // Add the completed polygon to the map
      const sourceId = 'field-drawing-polygon';
      const layerId = 'field-drawing-polygon-layer';

      // Remove existing source and layer if they exist
      if (mapRef.current.getLayer(layerId)) {
        mapRef.current.removeLayer(layerId);
      }
      if (mapRef.current.getSource(sourceId)) {
        mapRef.current.removeSource(sourceId);
      }

      // Add new source and layer
      mapRef.current.addSource(sourceId, {
        type: 'geojson',
        data: polygonGeoJSON as any
      });

      mapRef.current.addLayer({
        id: layerId,
        type: 'fill',
        source: sourceId,
        paint: {
          'fill-color': '#FF0000',
          'fill-opacity': 0.3
        }
      });

      mapRef.current.addLayer({
        id: layerId + '-stroke',
        type: 'line',
        source: sourceId,
        paint: {
          'line-color': '#FF0000',
          'line-width': 2
        }
      });

      // TODO: Here you would typically call a callback to pass the polygon data to the parent component
      console.log('Field drawing completed:', polygonGeoJSON);
    }
  };

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current) return;

    // Create map if it doesn't exist
    if (!mapRef.current) {
      mapRef.current = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: mapStyle,
        center: [-98.5795, 39.8283], // Center of US
        zoom: 4,
      });

              mapRef.current.on('load', () => {
          // Set map style as loaded when map is ready
          setMapStyleLoaded(true);

          // Expose the loadCountyTiles function to parent component
          if (onMapLoad) {
            const mapWithFunctions = mapRef.current as any;
            mapWithFunctions.loadCountyTiles = loadCountyTiles;
            mapWithFunctions.clearCountyData = () => clearCountyData();
            mapWithFunctions.getFieldDrawingPoints = getFieldDrawingPoints;
            mapWithFunctions.completeFieldDrawing = completeFieldDrawing;
            mapWithFunctions.clearFieldDrawing = clearFieldDrawing;
            mapWithFunctions.switchToSatelliteMode = switchToSatelliteMode;
            onMapLoad(mapRef.current!);
          }

          // Notify parent that map is ready
          if (onMapReady && mapRef.current) {
            const mapWithFunctions = mapRef.current as any;
            mapWithFunctions.loadCountyTiles = loadCountyTiles;
            mapWithFunctions.clearCountyData = () => clearCountyData();
            mapWithFunctions.getFieldDrawingPoints = getFieldDrawingPoints;
            mapWithFunctions.completeFieldDrawing = completeFieldDrawing;
            mapWithFunctions.clearFieldDrawing = clearFieldDrawing;
            mapWithFunctions.switchToSatelliteMode = switchToSatelliteMode;
            onMapReady(true); // Pass true to indicate map is ready
          }

        // Add moveend handler for bounds persistence
        if (mapRef.current) {
          mapRef.current.on('moveend', () => {
            if (onMapMove) {
              onMapMove();
            }
            // Update visible crops on map movement
            updateVisibleCrops();
          });
        }

        // Add zoomend handler for visible crops
        if (mapRef.current) {
          mapRef.current.on('zoomend', () => {
            updateVisibleCrops();
          });
        }

        // Add styledata event listener to track when style is loaded
        mapRef.current!.on('styledata', () => {
          if (mapRef.current?.isStyleLoaded()) {
            setMapStyleLoaded(true);

          }
        });

        // Also set mapStyleLoaded when map is initially loaded
        if (mapRef.current?.isStyleLoaded()) {
          setMapStyleLoaded(true);

        }
      });

      mapRef.current.on('error', (e: any) => {
        console.error('Map error:', e);
      });
    } else {
      // Map already exists, only update style if it actually changed
      const currentStyle = mapRef.current.getStyle().name || mapRef.current.getStyle().sprite;
      if (currentStyle !== mapStyle) {
        console.log('🔄 Updating map style from', currentStyle, 'to', mapStyle);
        mapRef.current.setStyle(mapStyle);
      }
    }

    // Cleanup function
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [mapStyle]); // Remove onMapLoad and onMapReady from dependencies to prevent re-initialization

  // Separate useEffect for handling drawing mode changes
  useEffect(() => {
    if (!mapRef.current) return;

    if (isDrawingMode) {
      // Enable drawing mode WITHOUT changing map state
      isDrawingRef.current = true;
      // Only set crosshair cursor if not in select mode
      if (!isSelectMode) {
        mapRef.current.getCanvas().style.cursor = 'crosshair';
      }

      // DO NOT disable map dragging - keep map fully interactive
      // mapRef.current.dragPan.disable(); // REMOVED - this was causing the issue

      // Add event listeners for drawing
      mapRef.current.on('mousedown', handleMouseDown);
      mapRef.current.on('mouseup', handleMouseUp);
      mapRef.current.on('mousemove', handleMouseMove);

    } else {
      // Disable drawing mode
      isDrawingRef.current = false;
      // Only reset cursor if not in select mode
      if (!isSelectMode) {
        mapRef.current.getCanvas().style.cursor = '';
      }
      startPointRef.current = null;

      // Map dragging should already be enabled, no need to re-enable
      // mapRef.current.dragPan.enable(); // REMOVED - not needed

      // Clean up rectangle marker
      if (rectangleRef.current) {
        rectangleRef.current.remove();
        rectangleRef.current = null;
      }

      // Clean up rectangle source and layer
      if (rectangleSourceRef.current && mapRef.current.getSource(rectangleSourceRef.current)) {
        if (mapRef.current.getLayer('drawing-rectangle')) {
          mapRef.current.removeLayer('drawing-rectangle');
        }
        if (mapRef.current.getLayer('drawing-rectangle-fill')) {
          mapRef.current.removeLayer('drawing-rectangle-fill');
        }
        mapRef.current.removeSource(rectangleSourceRef.current);
        rectangleSourceRef.current = null;
      }

      // Remove event listeners
      mapRef.current.off('mousedown', handleMouseDown);
      mapRef.current.off('mouseup', handleMouseUp);
      mapRef.current.off('mousemove', handleMouseMove);
    }

    // Cleanup function for drawing mode
    return () => {
      if (mapRef.current && isDrawingMode) {
        mapRef.current.off('mousedown', handleMouseDown);
        mapRef.current.off('mouseup', handleMouseUp);
        mapRef.current.off('mousemove', handleMouseMove);
      }
    };
  }, [isDrawingMode, isSelectMode]); // Add isSelectMode to dependencies

  // Separate useEffect for handling select mode changes
  useEffect(() => {
    if (!mapRef.current) return;

    if (isSelectMode) {
      // Enable select mode - disable map dragging to allow polygon clicks
      mapRef.current.dragPan.disable();
      // Only set pointer cursor if not in drawing mode
      if (!isDrawingMode) {
        mapRef.current.getCanvas().style.cursor = 'pointer';
      }
    } else {
      // Disable select mode - re-enable map dragging for normal navigation
      mapRef.current.dragPan.enable();
      // Only reset cursor if not in drawing mode
      if (!isDrawingMode) {
        mapRef.current.getCanvas().style.cursor = '';
      }
    }

    // Cleanup function for select mode
    return () => {
      if (mapRef.current && isSelectMode) {
        mapRef.current.dragPan.enable();
        if (!isDrawingMode) {
          mapRef.current.getCanvas().style.cursor = '';
        }
      }
    };
  }, [isSelectMode, isDrawingMode]);

  // Separate useEffect for handling field drawing mode changes
  useEffect(() => {
    if (!mapRef.current) return;

    if (isFieldDrawingMode) {
      // Enable field drawing mode
      mapRef.current.getCanvas().style.cursor = 'crosshair';
      // Disable map dragging to allow precise field drawing
      mapRef.current.dragPan.disable();
      
      // Add event listeners for field drawing
      mapRef.current.on('click', handleFieldDrawingClick);
      
    } else {
      // Disable field drawing mode
      if (!isDrawingMode && !isSelectMode) {
        mapRef.current.getCanvas().style.cursor = '';
      }
      // Re-enable map dragging if not in select mode
      if (!isSelectMode) {
        mapRef.current.dragPan.enable();
      }
      
      // Remove event listeners
      mapRef.current.off('click', handleFieldDrawingClick);
      
      // Check if we have enough points to keep the polygon
      const hasEnoughPoints = fieldDrawingPointsRef.current.length >= 3;
      
      if (!hasEnoughPoints) {
        // Clean up field drawing elements only if we don't have enough points
        fieldDrawingPointsRef.current = [];
        
        // Remove all field drawing markers
        fieldDrawingMarkersRef.current.forEach(marker => {
          if (marker) {
            marker.remove();
          }
        });
        fieldDrawingMarkersRef.current = [];
        
        if (fieldDrawingLineRef.current) {
          fieldDrawingLineRef.current.remove();
          fieldDrawingLineRef.current = null;
        }
        
        // Remove field drawing layers
        if (mapRef.current.getLayer('field-drawing-line-layer')) {
          mapRef.current.removeLayer('field-drawing-line-layer');
        }
        if (mapRef.current.getSource('field-drawing-line')) {
          mapRef.current.removeSource('field-drawing-line');
        }
        if (mapRef.current.getLayer('field-drawing-polygon-layer')) {
          mapRef.current.removeLayer('field-drawing-polygon-layer');
        }
        if (mapRef.current.getLayer('field-drawing-polygon-stroke-layer')) {
          mapRef.current.removeLayer('field-drawing-polygon-stroke-layer');
        }
        if (mapRef.current.getSource('field-drawing-polygon')) {
          mapRef.current.removeSource('field-drawing-polygon');
        }
      } else {
        // If we have enough points, just clear the markers but keep the polygon
        fieldDrawingMarkersRef.current.forEach(marker => {
          if (marker) {
            marker.remove();
          }
        });
        fieldDrawingMarkersRef.current = [];
        
        // Keep the polygon and line layers for the completed field
        // The polygon will remain visible on the map
      }
    }

    // Cleanup function for field drawing mode
    return () => {
      if (mapRef.current && isFieldDrawingMode) {
        mapRef.current.off('click', handleFieldDrawingClick);
      }
    };
  }, [isFieldDrawingMode, isDrawingMode, isSelectMode]);

  // Effect to handle field editing mode
  useEffect(() => {
    if (!mapRef.current || !isFieldDrawingMode || fieldDrawingPointsRef.current.length < 3) {
      return;
    }
    
    if (isFieldEditingMode) {
      // Enter edit mode
      // Remove any existing gl-draw-* layers before creating new draw tool
      // This prevents "layer already exists" errors when re-entering edit mode
      if (mapRef.current && mapRef.current.isStyleLoaded()) {
        try {
          const allLayers = mapRef.current.getStyle().layers || [];
          const glDrawLayers = allLayers.filter((layer: any) => 
            layer.id && typeof layer.id === 'string' && layer.id.startsWith('gl-draw-')
          );
          // Remove layers in reverse order to avoid dependency issues
          glDrawLayers.reverse().forEach((layer: any) => {
            try {
              if (mapRef.current && mapRef.current.getLayer(layer.id)) {
                mapRef.current.removeLayer(layer.id);
              }
            } catch (error) {
              // Layer might already be removed, ignore
            }
          });
        } catch (error) {
          // If style isn't ready yet, continue anyway
        }
      }

      if (!fieldDrawingDrawRef.current) {
        try {
          // Initialize MapboxDraw for edit mode without custom snap modes to avoid coordinate errors
          fieldDrawingDrawRef.current = new MapboxDraw({
          displayControlsDefault: false,
          controls: {
            point: false,
            line_string: false,
            polygon: false,
            trash: false
          },
          modes: {
            ...MapboxDraw.modes,
            // Use custom snap modes for drawing, but standard direct_select to avoid crashes
            draw_point: SnapPointMode as any,
            draw_polygon: SnapPolygonMode as any,
            draw_line_string: SnapLineMode as any,
            direct_select: MapboxDraw.modes.direct_select, // Use standard mode to avoid coordinate errors
          } as any,
          userProperties: true,
          snap: true as any,
          snapOptions: {
            snapPx: 9,
            snapToMidPoints: true,
            snapVertexPriorityDistance: 0.0025,
            snapGetFeatures: (map: any, draw: any) => {
              // Get existing fields to snap to
              const existingFeatures = existingFields.map(field => {
                try {
                  const geojson = typeof field.geojson === 'string' ? JSON.parse(field.geojson) : field.geojson;
                  // Validate that the geometry has valid coordinates
                  if (!geojson || !geojson.coordinates || !Array.isArray(geojson.coordinates)) {
                    return null;
                  }
                  // For polygons, ensure coordinates array is valid
                  if (geojson.type === 'Polygon' && (!geojson.coordinates[0] || geojson.coordinates[0].length < 3)) {
                    return null;
                  }
                  return {
                    type: 'Feature' as const,
                    geometry: geojson,
                    properties: { field_id: field.field_id }
                  };
                } catch (e) {
                  return null;
                }
              }).filter(f => f !== null);
              
              // Also validate draw features before returning
              const drawFeatures = draw.getAll().features.filter((feature: any) => {
                return feature && 
                       feature.geometry && 
                       feature.geometry.coordinates && 
                       Array.isArray(feature.geometry.coordinates);
              });
              
              return [
                ...existingFeatures,
                ...drawFeatures
              ];
            }
          },
          guides: false,
          styles: (() => {
            // Custom styles - only include ones that override SnapModeDrawStyles or are new
            const customStyles = [
              {
                "id": "gl-draw-polygon-fill-active",
                "type": "fill",
                "filter": ["all", ["==", "$type", "Polygon"], ["!=", "mode", "static"]],
                "paint": {
                  "fill-color": "#FF0000",
                  "fill-outline-color": "#FF0000",
                  "fill-opacity": 0.2
                }
              },
              {
                "id": "gl-draw-polygon-stroke-active",
                "type": "line",
                "filter": ["all", ["==", "$type", "Polygon"], ["!=", "mode", "static"]],
                "layout": {
                  "line-cap": "round",
                  "line-join": "round"
                },
                "paint": {
                  "line-color": "#FF0000",
                  "line-dasharray": ["literal", [0.2, 2]],
                  "line-width": 2
                }
              },
              {
                "id": "gl-draw-polygon-midpoint",
                "type": "circle",
                "filter": ["all", ["==", "$type", "Point"], ["==", "meta", "midpoint"]],
                "paint": {
                  "circle-radius": 3,
                  "circle-color": "#FF0000"
                }
              },
              {
                "id": "gl-draw-polygon-vertex-active",
                "type": "circle",
                "filter": ["all", ["==", "meta", "vertex"], ["==", "$type", "Point"], ["!=", "mode", "static"]],
                "paint": {
                  "circle-radius": 4,
                  "circle-color": "#fff",
                  "circle-stroke-color": "#FF0000",
                  "circle-stroke-width": 2
                }
              },
              {
                "id": "gl-draw-polygon-vertex-inactive",
                "type": "circle",
                "filter": ["all", ["==", "meta", "vertex"], ["==", "$type", "Point"], ["==", "mode", "static"]],
                "paint": {
                  "circle-radius": 3,
                  "circle-color": "#fff",
                  "circle-stroke-color": "#FF0000",
                  "circle-stroke-width": 1
                }
              },
              {
                "id": "gl-draw-point-snap",
                "type": "circle",
                "filter": ["==", "meta", "snap"],
                "paint": {
                  "circle-radius": 5,
                  "circle-color": "#FF0000",
                  "circle-stroke-color": "#fff",
                  "circle-stroke-width": 2
                }
              }
            ];
            
            // Filter out SnapModeDrawStyles that we're overriding with custom styles
            const filteredSnapStyles = (SnapModeDrawStyles || []).filter((s: any) => {
              // Keep styles that aren't being overridden by our custom styles
              return !customStyles.some(cs => cs.id === s.id);
            });
            
            // Return: filtered snap styles first, then custom overrides (custom ones will override)
            // Note: MapboxDraw processes styles in order, so later definitions override earlier ones
            return [...filteredSnapStyles, ...customStyles];
          })()
        } as any);
        
        // Add draw tool to map
        mapRef.current.addControl(fieldDrawingDrawRef.current);
        } catch (error) {
          console.error('Error initializing MapboxDraw for edit mode:', error);
          return;
        }
      }
      
      // Create polygon from current points and load into draw tool
      const points = fieldDrawingPointsRef.current;
      
      // Validate points before creating polygon
      if (!points || points.length < 3) {
        return;
      }
      
      // Ensure all points are valid coordinates
      const validPoints = points.filter(point => 
        point && 
        Array.isArray(point) && 
        point.length === 2 && 
        typeof point[0] === 'number' && 
        typeof point[1] === 'number' &&
        !isNaN(point[0]) && 
        !isNaN(point[1])
      );
      
      if (validPoints.length < 3) {
        return;
      }
      
      const polygonFeature = {
        type: 'Feature' as const,
        geometry: {
          type: 'Polygon' as const,
          coordinates: [[...validPoints, validPoints[0]]] as [number, number][][] // Close the polygon
        },
        properties: {}
      };
      
      // Clear and add the polygon
      if (fieldDrawingDrawRef.current) {
        fieldDrawingDrawRef.current.deleteAll();
        const featureIds = fieldDrawingDrawRef.current.add(polygonFeature as any);
        
        // Enable direct select mode for editing
        if (featureIds && featureIds.length > 0) {
          fieldDrawingDrawRef.current.changeMode('direct_select', { featureId: featureIds[0] });
        }
      }
      
      // Listen for updates from draw tool
      const handleDrawUpdate = () => {
        if (fieldDrawingDrawRef.current) {
          const data = fieldDrawingDrawRef.current.getAll();
          if (data.features.length > 0) {
            const feature = data.features[0];
            if (feature.geometry.type === 'Polygon') {
              // Extract coordinates (remove last point which is duplicate of first)
              // Create a copy to avoid mutating the original MapboxDraw geometry
              const coords = [...(feature.geometry as any).coordinates[0]];
              coords.pop(); // Remove closing point from the copy
              fieldDrawingPointsRef.current = coords as [number, number][];
              
              // Update visualization
              updateFieldDrawingVisualization();
            }
          }
        }
      };
      
      if (mapRef.current) {
        mapRef.current.on('draw.update', handleDrawUpdate);
        mapRef.current.on('draw.delete', handleDrawUpdate);
      }
      
      // Cleanup
      return () => {
        if (mapRef.current) {
          mapRef.current.off('draw.update', handleDrawUpdate);
          mapRef.current.off('draw.delete', handleDrawUpdate);
        }
      };
    } else {
      // Exit edit mode
      if (fieldDrawingDrawRef.current && mapRef.current) {
        const data = fieldDrawingDrawRef.current.getAll();
        if (data.features.length > 0) {
          const feature = data.features[0];
          if (feature.geometry.type === 'Polygon') {
            // Extract updated coordinates
            // Create a copy to avoid mutating the original MapboxDraw geometry
            const coords = [...(feature.geometry as any).coordinates[0]];
            coords.pop(); // Remove closing point from the copy
            fieldDrawingPointsRef.current = coords as [number, number][];
            
            // Update visualization
            updateFieldDrawingVisualization();
          }
        }
        
        // Remove draw tool
        if (mapRef.current && fieldDrawingDrawRef.current) {
          try {
            fieldDrawingDrawRef.current.deleteAll();
            mapRef.current.removeControl(fieldDrawingDrawRef.current);
            // Also remove any remaining gl-draw-* layers that might persist
            if (mapRef.current.isStyleLoaded()) {
              const allLayers = mapRef.current.getStyle().layers || [];
              const glDrawLayers = allLayers.filter((layer: any) => 
                layer.id && typeof layer.id === 'string' && layer.id.startsWith('gl-draw-')
              );
              glDrawLayers.reverse().forEach((layer: any) => {
                try {
                  if (mapRef.current && mapRef.current.getLayer(layer.id)) {
                    mapRef.current.removeLayer(layer.id);
                  }
                } catch (e) {}
              });
            }
          } catch (e) {
            console.warn('Failed to remove draw control:', e);
          }
        }
        fieldDrawingDrawRef.current = null;
      }
    }
  }, [isFieldEditingMode, isFieldDrawingMode]);

  // Separate useEffect for managing click events based on select mode
  useEffect(() => {
    if (!mapRef.current || !onPolygonClick || !currentCountyKey) return;

    // Check if map style is loaded
    if (!mapRef.current.isStyleLoaded()) {
      return;
    }

    const handleMapClick = (e: any) => {
      if (!isSelectMode) return; // Only allow clicking when in select mode
      if (isFieldEditingMode) return; // Don't handle clicks during field editing to prevent state conflicts

      // Get all available layers with their IDs
      const style = mapRef.current!.getStyle();
      const allLayers = style.layers || [];
      const layerIds = allLayers.map((layer: any) => layer.id);

      // Filter for crop layers
      const cropLayers = layerIds.filter((layerId: string) => layerId.includes(`crops-fill-`));

      const features = mapRef.current!.queryRenderedFeatures(e.point, {
        layers: cropLayers
      });

      if (features.length > 0) {
        const feature = features[0];

        // Change stroke color to red for selected polygon only
        if (currentCountyKey) {
          // Since stroke layer coordinates differ from fill layer, let's use a different approach
          // Add a temporary marker at the click point to show selection
          const clickLngLat = e.lngLat;

          // Remove any existing selection marker
          if (mapRef.current!.getLayer('selection-marker')) {
            mapRef.current!.removeLayer('selection-marker');
          }
          if (mapRef.current!.getSource('selection-marker')) {
            mapRef.current!.removeSource('selection-marker');
          }

          // Add a selection marker
          mapRef.current!.addSource('selection-marker', {
            type: 'geojson',
            data: {
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [clickLngLat.lng, clickLngLat.lat]
              },
              properties: {}
            }
          });

          mapRef.current!.addLayer({
            id: 'selection-marker',
            type: 'circle',
            source: 'selection-marker',
            paint: {
              'circle-radius': 8,
              'circle-color': '#FF0000',
              'circle-stroke-color': '#FFFFFF',
              'circle-stroke-width': 2
            }
          });
        }

        onPolygonClick(feature);
      }
    };

    if (isSelectMode) {
      // Add click handler when in select mode
      mapRef.current.on('click', handleMapClick);
    } else {
      // Remove click handler when not in select mode
      mapRef.current.off('click', handleMapClick);
    }

    // Cleanup function
    return () => {
      if (mapRef.current) {
        mapRef.current.off('click', handleMapClick);
      }
    };
  }, [isSelectMode, isFieldSelectionMode, onPolygonClick]);

  // Handle existing fields visualization
  useEffect(() => {
    
    
    if (!mapRef.current || !isFieldSelectionMode) {
     
      return;
    }

    // Check if map style is loaded
    if (!mapStyleLoaded) {
     
      return;
    }

    // Remove existing fields layer if it exists
    if (mapRef.current.getLayer('existing-fields-fill')) {
      mapRef.current.removeLayer('existing-fields-fill');
    }
    if (mapRef.current.getLayer('existing-fields-stroke')) {
      mapRef.current.removeLayer('existing-fields-stroke');
    }
    if (mapRef.current.getSource('existing-fields')) {
      mapRef.current.removeSource('existing-fields');
    }

    // Add existing fields if any
    if (existingFields.length > 0) {
      
      const existingFieldsGeoJSON: any = {
        type: 'FeatureCollection',
        features: existingFields.map(field => ({
          type: 'Feature',
          geometry: JSON.parse(field.geojson),
          properties: {
            id: field.field_id,
            name: field.fieldname,
            acres: field.acres
          }
        }))
      };


      mapRef.current.addSource('existing-fields', {
        type: 'geojson',
        data: existingFieldsGeoJSON
      });

      // Add fill layer for existing fields (blue) - add at the end to be on top
      mapRef.current.addLayer({
        id: 'existing-fields-fill',
        type: 'fill',
        source: 'existing-fields',
        paint: {
          'fill-color': '#2196F3',
          'fill-opacity': 0.3
        }
      });

      // Add stroke layer for existing fields (blue border) - add at the end to be on top
      mapRef.current.addLayer({
        id: 'existing-fields-stroke',
        type: 'line',
        source: 'existing-fields',
        paint: {
          'line-color': '#2196F3',
          'line-width': 1
        }
      });
    }
      }, [existingFields, isFieldSelectionMode, mapStyleLoaded]);

  const toggleMapStyle = () => {
    if (!mapRef.current) return;

    // Store current view and county data
    const currentCenter = mapRef.current.getCenter();
    const currentZoom = mapRef.current.getZoom();
    const currentCounty = currentCountyKey;

    setMapStyle(prevStyle => {
      const newStyle = prevStyle === "mapbox://styles/mapbox/streets-v12"
        ? "mapbox://styles/mapbox/satellite-v9"
        : "mapbox://styles/mapbox/streets-v12";

      // Calculate the new opacity based on the new style
      const newOpacity = 0.1; //newStyle.includes('satellite') ? 0.2 : 0.6;

      // Update the map style directly
      mapRef.current!.setStyle(newStyle);

      // After style change, restore view and reload county data
      // Use once to prevent multiple listeners
      const styleLoadHandler = () => {
        // Restore the view immediately without any animation
        mapRef.current!.flyTo({
          center: currentCenter,
          zoom: currentZoom,
          duration: 0 // No animation to prevent zoom issues
        });

        // Reload county data if we had any (without zooming)
        if (currentCounty) {
          setTimeout(() => {
            loadCountyTiles(currentCounty);
          }, 100);
        }

        // Update opacity of existing polygons if any are loaded
        if (currentCounty) {
          const fillLayerId = `crops-fill-${currentCounty}`;
          if (mapRef.current!.getLayer(fillLayerId)) {
            mapRef.current!.setPaintProperty(fillLayerId, 'fill-opacity', newOpacity);
          }
        }

        // Re-add existing fields layers after style change
        setTimeout(() => {
          addExistingFieldsLayers();
        }, 200);
      };

      // Use once to ensure the event is only handled once
      mapRef.current!.once('style.load', styleLoadHandler);

      return newStyle;
    });
  };

  // Function to switch to satellite mode (can be called from parent)
  const switchToSatelliteMode = () => {
    if (!mapRef.current) return;

    // Only switch if not already in satellite mode
    if (!mapStyle.includes('satellite')) {
      // Store current view and county data
      const currentCenter = mapRef.current.getCenter();
      const currentZoom = mapRef.current.getZoom();
      const currentCounty = currentCountyKey;

      const newStyle = "mapbox://styles/mapbox/satellite-v9";
      const newOpacity = 0.1;

      // Update the map style directly
      mapRef.current.setStyle(newStyle);

      // After style change, restore view and reload county data
      // Use once to prevent multiple listeners
      const styleLoadHandler = () => {
        // Restore the view immediately without any animation
        mapRef.current!.flyTo({
          center: currentCenter,
          zoom: currentZoom,
          duration: 0 // No animation to prevent zoom issues
        });

        // Reload county data if we had any (loadCountyTiles doesn't zoom)
        if (currentCounty) {
          setTimeout(() => {
            loadCountyTiles(currentCounty);
          }, 100);
        }

        // Update opacity of existing polygons if any are loaded
        if (currentCounty) {
          const fillLayerId = `crops-fill-${currentCounty}`;
          if (mapRef.current!.getLayer(fillLayerId)) {
            mapRef.current!.setPaintProperty(fillLayerId, 'fill-opacity', newOpacity);
          }
        }

        // Re-add existing fields layers after style change
        setTimeout(() => {
          addExistingFieldsLayers();
        }, 200);
      };

      // Use once to ensure the event is only handled once
      mapRef.current.once('style.load', styleLoadHandler);

      setMapStyle(newStyle);
    }
  };

  return (
    <div style={{ position: "relative" }}>
      <div ref={mapContainerRef} style={{ width: "100%", height: "100vh" }} />

      {/* Loading indicator */}
      {isLoading && (
        <div style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          color: "white",
          padding: "20px",
          borderRadius: "8px",
          zIndex: 1000,
          fontSize: "16px"
        }}>
          Loading county data...
        </div>
      )}

      {/* Drawing mode indicator */}
      {isDrawingMode && (
        <div style={{
          position: "absolute",
          top: 10,
          left: 10,
          backgroundColor: "rgba(255, 0, 0, 0.8)",
          color: "white",
          padding: "8px 12px",
          borderRadius: "4px",
          zIndex: 1000,
          fontSize: "14px"
        }}>
          🖊️ Drawing Mode - Click and drag to draw rectangle
        </div>
      )}

      {/* Style toggle button */}
      <button
        onClick={toggleMapStyle}
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          zIndex: 1000,
          padding: "8px 12px",
          fontSize: "0.9em",
          border: "1px solid #28a745",
          borderRadius: "4px",
          backgroundColor: "#28a745",
          color: "white",
          cursor: "pointer",
          transition: "all 0.2s"
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.backgroundColor = "#218838";
          e.currentTarget.style.borderColor = "#218838";
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.backgroundColor = "#28a745";
          e.currentTarget.style.borderColor = "#28a745";
        }}
      >
        {mapStyle.includes('satellite') ? 'Street View' : 'Satellite View'}
      </button>
    </div>
  );
});

export default MapboxMap;
